import { useCallback, useState } from 'react'
import { CheckCircle2, Circle, CircleCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useUpdateMachineryDirectiveComplianceCompleted } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type {
  DeclarationOfConformityRequirement,
  DeclarationOfConformityRequirementKey,
} from '../hooks/useDeclarationOfConformityRequirements'
import { MarkCompleteComplianceDialog } from './MarkCompleteComplianceDialog'

const REQUIREMENT_EXPLANATION_KEYS: Record<
  DeclarationOfConformityRequirementKey,
  string
> = {
  manufacturerAssigned:
    'settings.machineryDirectiveDeclarationOfConformity.warningNoManufacturer',
  authorisedRepresentativeAssigned:
    'settings.machineryDirectiveDeclarationOfConformity.warningNoAuthorisedRepresentative',
  atLeastOneStandardAssigned:
    'settings.machineryDirectiveDeclarationOfConformity.warningNoStandardAssigned',
  complianceStudioComplete:
    'intro.declarationOfConformityChecklist.explanationComplianceStudioComplete',
}

const REQUIREMENT_ROUTE_SEGMENT: Partial<
  Record<DeclarationOfConformityRequirementKey, string>
> = {
  atLeastOneStandardAssigned: 'standards',
  manufacturerAssigned: '/settings/associated-parties',
  authorisedRepresentativeAssigned: '/settings/associated-parties',
}

function MarkCompleteChecklistAction({
  complianceId,
  isComplete,
}: {
  complianceId: string
  isComplete: boolean
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [dialogOpen, setDialogOpen] = useState(false)
  const updateCompleted =
    useUpdateMachineryDirectiveComplianceCompleted(complianceId)

  const handleConfirm = useCallback(async () => {
    try {
      await updateCompleted.mutateAsync({ completed: true })
      toast.success(t('markComplete.dialog.successToast'))
      setDialogOpen(false)
    } catch {
      toast.error(t('markComplete.dialog.failedToast'))
    }
  }, [updateCompleted, t])

  if (isComplete) {
    return (
      <Badge
        variant='default'
        className='mt-2 w-fit gap-1.5 bg-green-600 py-1.5 font-medium text-white'
      >
        <CheckCircle2 className='size-4' aria-hidden />
        {t('markComplete.completedLabel')}
      </Badge>
    )
  }

  return (
    <>
      <Button
        variant='outline'
        size='sm'
        className='mt-2'
        onClick={() => {
          setDialogOpen(true)
        }}
      >
        <CircleCheck className='me-2 size-4 shrink-0' aria-hidden />
        {t('markComplete.button')}
      </Button>
      <MarkCompleteComplianceDialog
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false)
        }}
        onConfirm={handleConfirm}
        isSubmitting={updateCompleted.isPending}
      />
    </>
  )
}

export function DeclarationOfConformityRequirementsChecklist({
  isPartlyCompletedMachine,
  complianceId,
  buildHref,
  requirements,
}: {
  isPartlyCompletedMachine: boolean
  complianceId: string
  buildHref: (routeSegment?: string) => string
  requirements: DeclarationOfConformityRequirement[]
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  const checklistTitle = isPartlyCompletedMachine
    ? 'intro.declarationOfIncorporationChecklist.title'
    : 'intro.declarationOfConformityChecklist.title'

  return (
    <div
      className='rounded-lg border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/50 dark:bg-amber-950/30'
      role='region'
      aria-label={t(checklistTitle)}
    >
      <h3 className='mb-3 text-sm font-semibold text-foreground'>
        {t(checklistTitle)}
      </h3>
      <p className='mb-4 text-sm text-muted-foreground'>
        {t('declarationOfConformityPage.requirementsBeforeGenerate')}
      </p>
      <ul className='space-y-4' role='list'>
        {requirements.map((requirement) => (
          <li key={requirement.key} className='flex gap-3'>
            {requirement.met ? (
              <CheckCircle2
                className='size-5 shrink-0 translate-y-1 text-green-600 dark:text-green-500'
                aria-hidden
              />
            ) : (
              <Circle
                className='size-5 shrink-0 translate-y-1 text-muted-foreground'
                aria-hidden
              />
            )}
            <div className='min-w-0 flex-1'>
              <h4
                className={cn(
                  requirement.met ? 'text-foreground' : 'text-muted-foreground',
                  'text-sm font-medium'
                )}
              >
                {t(`intro.declarationOfConformityChecklist.${requirement.key}`)}
              </h4>
              {requirement.key === 'complianceStudioComplete' &&
              requirement.met ? (
                <p className='mt-1 text-sm text-muted-foreground'>
                  {t(
                    'intro.declarationOfConformityChecklist.complianceStudioCompleteMet'
                  )}
                </p>
              ) : (
                <p className='mt-1 text-sm text-muted-foreground'>
                  {t(REQUIREMENT_EXPLANATION_KEYS[requirement.key])}
                </p>
              )}
              {requirement.key === 'complianceStudioComplete' ? (
                <MarkCompleteChecklistAction
                  complianceId={complianceId}
                  isComplete={requirement.met}
                />
              ) : (
                <Button variant='outline' size='sm' className='mt-2' asChild>
                  <Link
                    to={buildHref(
                      REQUIREMENT_ROUTE_SEGMENT[requirement.key] ?? ''
                    )}
                  >
                    {t(
                      `intro.declarationOfConformityChecklist.action.${requirement.key}`
                    )}
                  </Link>
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
